import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const relationshipGoalOptions = [
  { value: 'friendship', label: 'Friendship', description: 'Looking for travel buddies' },
  { value: 'dating', label: 'Dating', description: 'Open to romance' },
  { value: 'serious_relationship', label: 'Serious Relationship', description: 'Looking for something long-term' },
  { value: 'marriage', label: 'Marriage', description: 'Seeking a life partner' },
  { value: 'open_to_all', label: 'Open to All', description: "Let's see where it goes" },
];

const RelationshipGoalsStep = ({ formData, updateFormData }) => {
  const toggleGoal = (goal) => {
    const current = formData.relationship_goals || [];
    if (current.includes(goal)) {
      updateFormData({
        relationship_goals: current.filter(g => g !== goal)
      });
    } else {
      updateFormData({
        relationship_goals: [...current, goal]
      });
    }
  };
  
  return (
    <div className="space-y-6">
      <p className="text-neutral-600 mb-6">
        What are you looking for in your travel connections?
      </p>
      
      {/* Relationship Goals */}
      <div className="space-y-3">
        <Label>I'm looking for... *</Label>
        <p className="text-sm text-neutral-500">Select all that apply</p>
        <div className="space-y-3">
          {relationshipGoalOptions.map((option) => (
            <div
              key={option.value}
              onClick={() => toggleGoal(option.value)}
              className={`
                p-4 rounded-lg border-2 cursor-pointer transition-all
                ${formData.relationship_goals?.includes(option.value)
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-neutral-200 hover:border-neutral-300'
                }
              `}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-semibold text-primary-900 mb-1">{option.label}</h4>
                  <p className="text-sm text-neutral-600">{option.description}</p>
                </div>
                <div className={`
                  w-5 h-5 rounded border-2 flex items-center justify-center transition-all
                  ${formData.relationship_goals?.includes(option.value)
                    ? 'border-primary-500 bg-primary-500'
                    : 'border-neutral-300'
                  }
                `}>
                  {formData.relationship_goals?.includes(option.value) && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Cultural Background */}
      <div className="space-y-2">
        <Label htmlFor="cultural_background">Cultural Background (Optional)</Label>
        <Input
          id="cultural_background"
          type="text"
          placeholder="e.g., Desi, Middle Eastern, Latin American"
          value={formData.cultural_background}
          onChange={(e) => updateFormData({ cultural_background: e.target.value })}
        />
        <p className="text-xs text-neutral-500">Help us connect you with people who share your values</p>
      </div>
      
      {/* Religion */}
      <div className="space-y-2">
        <Label htmlFor="religion">Religion/Spirituality (Optional)</Label>
        <Select
          value={formData.religion}
          onValueChange={(value) => updateFormData({ religion: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No religion</SelectItem>
            <SelectItem value="christian">Christian</SelectItem>
            <SelectItem value="muslim">Muslim</SelectItem>
            <SelectItem value="hindu">Hindu</SelectItem>
            <SelectItem value="buddhist">Buddhist</SelectItem>
            <SelectItem value="jewish">Jewish</SelectItem>
            <SelectItem value="sikh">Sikh</SelectItem>
            <SelectItem value="spiritual">Spiritual but not religious</SelectItem>
            <SelectItem value="other">Other</SelectItem>
            <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {/* Additional Info */}
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="height">Height (cm)</Label>
          <Input
            id="height"
            type="number"
            placeholder="170"
            value={formData.height}
            onChange={(e) => updateFormData({ height: e.target.value })}
          />
        </div>
        
        <div className="space-y-2">
          <Label>Alcohol</Label>
          <Select
            value={formData.alcohol === null ? '' : formData.alcohol ? 'yes' : 'no'}
            onValueChange={(value) => updateFormData({ alcohol: value === 'yes' ? true : value === 'no' ? false : null })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
              <SelectItem value="occasionally">Occasionally</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label>Smoking</Label>
          <Select
            value={formData.smoking === null ? '' : formData.smoking ? 'yes' : 'no'}
            onValueChange={(value) => updateFormData({ smoking: value === 'yes' ? true : value === 'no' ? false : null })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
              <SelectItem value="occasionally">Occasionally</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default RelationshipGoalsStep;
